import { components as c } from "@/components";
import { zodResolver } from "@hookform/resolvers/zod";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { SubmitHandler, useForm } from "react-hook-form";
import { useDiaryByIdQuery } from "@/features/diaries";
import { InjectionSchemaT, InjectionSchemaZ } from "@/features/injection/types/injectionSchema";
import { useInjectionMutation } from "@/features/injection/api/upsertInjection";
import useExtractInjection from "@/features/injection/hooks/useExtractInjection";
import InjectionFormFields from "@/features/injection/components/InjectionFormFields";
import { ui } from "@patient-watch/ui";
import { useAuth } from "@/utils/auth";
import Stepper from "@/components/common/Stepper";
import { useEventStepper } from "@/features/templates/hooks/useEventNavigationStore";

const InjectionFormFieldsPage = () => {
    const { diaryId, eventId, injectionId } = Route.useParams();
    const auth = useAuth();
    const injection = useExtractInjection(diaryId, injectionId);
    const { data: diary } = useDiaryByIdQuery(diaryId);

    const navigate = useNavigate();

    const injectionMutation = useInjectionMutation({ diaryId: diaryId });

    const form = useForm<InjectionSchemaT>({
        resolver: zodResolver(InjectionSchemaZ),
        defaultValues: {
            event_id: eventId,
            injection_id: injectionId,
            referring_clinician_id: injection?.referring_clinician_id ?? auth.id,
            injection_method_id: injection?.injection_method_id ?? undefined,
            injecting_clinician_id: injection?.injecting_clinician_id ?? auth.id,
        },
    });

    const { steps } = useEventStepper("injection");

    const onSuccess = () => {
        navigate({
            to: "/templates/diary/$diaryId/events/$eventId/injection/$injectionId/details",
            params: { diaryId, eventId, injectionId },
        });
    };

    const onSubmit: SubmitHandler<InjectionSchemaT> = async (formData) => {
        console.log(formData);
        injectionMutation.mutate(formData, { onSuccess: onSuccess });
    };

    const handlePrevious = () => {
        navigate({
            to: "/templates/diary/$diaryId/events/$eventId/details",
            params: { diaryId, eventId },
        });
    };

    return (
        <div className="space-y-8">
            <Stepper steps={steps} />
            <ui.Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className="form">
                    <InjectionFormFields organisationId={diary?.organisation_id as string} />
                    <div className="flex justify-between gap-4 mt-8">
                        <c.fc.BackButton
                            type="button"
                            variant="outline"
                            label="Previous"
                            onClick={handlePrevious}
                            className="w-auto"
                        />
                        <c.fc.NextButton />
                    </div>
                </form>
            </ui.Form>
        </div>
    );
};

export const Route = createFileRoute("/_app/templates/diary/$diaryId/events/$eventId/injection/$injectionId/")({
    component: InjectionFormFieldsPage,
});
